/**
 * Top-bar notifications bell with unread count + dropdown.
 * Polls every 30s for new notifications.
 */

import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import type { Notification } from '@friday/shared/realtime'

export function NotificationsBell() {
  const [open, setOpen] = useState(false)
  const qc = useQueryClient()
  const navigate = useNavigate()

  const list = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<Notification[]>('/api/v1/me/notifications'),
    refetchInterval: 30_000,
  })

  const readAll = useMutation({
    mutationFn: () => api<{ ok: boolean }>('/api/v1/me/notifications/read-all', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })
  const readOne = useMutation({
    mutationFn: (nid: string) => api(`/api/v1/me/notifications/${nid}/read`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const items = list.data ?? []
  const unread = items.filter((n) => !n.readAt).length

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-md p-1.5 text-surface-600 hover:bg-surface-100 dark:text-surface-300 dark:hover:bg-surface-800"
        aria-label="Notifications"
      >
        <span className="text-base">🔔</span>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            onMouseLeave={() => setOpen(false)}
            // The bell sits at the right edge of the top bar, so the
            // dropdown hangs below it and opens leftward. z-50 sits above
            // the notebook page tree (z-40) and any other overlay surfaces
            // on the workspace shell.
            className="absolute right-0 top-full z-50 mt-2 w-96 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-lg border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 shadow-lg"
          >
            <header className="flex items-center justify-between border-b border-surface-200 dark:border-surface-700 px-3 py-2">
              <span className="text-sm font-semibold">Notifications</span>
              {unread > 0 && (
                <button
                  onClick={() => readAll.mutate()}
                  className="text-xs text-brand-500 hover:underline"
                >
                  Mark all read
                </button>
              )}
            </header>
            <div className="max-h-[60vh] overflow-y-auto">
              {list.isLoading && <div className="px-3 py-6 text-center text-xs text-surface-500">Loading…</div>}
              {!list.isLoading && items.length === 0 && (
                <div className="px-6 py-10 text-center">
                  <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-surface-100 text-lg dark:bg-surface-700">
                    ✨
                  </div>
                  <p className="text-sm text-surface-700 dark:text-surface-200">You're all caught up.</p>
                  <p className="mt-1 text-xs text-surface-500">
                    Mentions, assignments, and sprint events will show up here.
                  </p>
                </div>
              )}
              {items.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  onClick={() => {
                    readOne.mutate(n.id)
                    setOpen(false)
                    const taskKey = (n.payload as { taskKey?: string })?.taskKey
                    const workspaceId = (n.payload as { workspaceId?: string })?.workspaceId
                    const projectKey = (n.payload as { projectKey?: string })?.projectKey
                    if (taskKey && projectKey && workspaceId) {
                      void navigate({
                        to: '/w/$wsId/p/$pKey/t/$tKey',
                        params: { wsId: workspaceId, pKey: projectKey, tKey: taskKey },
                      })
                    }
                  }}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function NotificationRow({ n, onClick }: { n: Notification; onClick: () => void }) {
  const isUnread = !n.readAt
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-2 border-b border-surface-100 dark:border-surface-700 px-3 py-2 text-left text-sm hover:bg-surface-50 dark:hover:bg-surface-700',
        isUnread && 'bg-brand-500/5',
      )}
    >
      {isUnread && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />}
      {!isUnread && <span className="mt-1.5 h-1.5 w-1.5 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-surface-900 dark:text-surface-50">{summary(n)}</div>
        <div className="mt-0.5 text-[10px] text-surface-500">
          {new Date(n.createdAt).toLocaleString()}
        </div>
      </div>
    </button>
  )
}

function summary(n: Notification): string {
  const p = n.payload as Record<string, unknown>
  switch (n.kind) {
    case 'task.assigned':
      return `${p.byUser ?? 'Someone'} assigned you ${(p.taskKey as string) ?? 'a task'}`
    case 'task.mentioned':
      return `${p.byUser ?? 'Someone'} mentioned you in ${(p.taskKey as string) ?? 'a task'}`
    case 'task.commented':
      return `New comment on ${(p.taskKey as string) ?? 'a task'}`
    case 'sprint.started':
      return `Sprint started: ${(p.sprintName as string) ?? ''}`
    case 'sprint.completed':
      return `Sprint completed: ${(p.sprintName as string) ?? ''}`
    default:
      return n.kind
  }
}
